import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { allWorksheets } from './all-worksheets';
import { categories, subjects, topics } from './data';
import SEOHead from '../../components/feature/SEOHead';

export default function WorksheetsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize state from URL params
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('grade') || 'All');
  const [selectedSubject, setSelectedSubject] = useState('All Subjects');
  const [selectedTopic, setSelectedTopic] = useState(searchParams.get('topic') || 'All Topics');
  const [searchQuery, setSearchQuery] = useState('');

  // Sync state with URL params on mount and when URL changes
  useEffect(() => {
    const gradeParam = searchParams.get('grade');
    const topicParam = searchParams.get('topic');
    
    if (gradeParam) {
      setSelectedCategory(gradeParam);
    }
    if (topicParam) {
      setSelectedTopic(topicParam);
    }
  }, [searchParams]);

  // Update URL when filters change
  const updateFilters = (grade: string, topic: string) => {
    const params = new URLSearchParams();
    
    if (grade !== 'All') {
      params.set('grade', grade);
    }
    if (topic !== 'All Topics') {
      params.set('topic', topic);
    }
    
    setSearchParams(params);
  };

  const handleCategoryChange = (value: string) => {
    setSelectedCategory(value);
    updateFilters(value, selectedTopic);
  };

  const handleTopicChange = (value: string) => {
    setSelectedTopic(value);
    updateFilters(selectedCategory, value);
  };

  const clearFilters = () => {
    setSelectedCategory('All');
    setSelectedSubject('All Subjects');
    setSelectedTopic('All Topics');
    setSearchQuery('');
    setSearchParams(new URLSearchParams());
  };

  const filteredWorksheets = useMemo(() => {
    return allWorksheets.filter((worksheet) => {
      const matchesCategory = selectedCategory === 'All' || worksheet.grade === selectedCategory;
      const matchesSubject = selectedSubject === 'All Subjects' || worksheet.subject === selectedSubject;
      const matchesTopic = selectedTopic === 'All Topics' || worksheet.topic === selectedTopic;
      const matchesSearch = searchQuery === '' || 
        worksheet.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        worksheet.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        worksheet.topic.toLowerCase().includes(searchQuery.toLowerCase());
      
      return matchesCategory && matchesSubject && matchesTopic && matchesSearch;
    });
  }, [selectedCategory, selectedSubject, selectedTopic, searchQuery]);

  const freeCount = filteredWorksheets.filter(w => w.isFree).length;
  const premiumCount = filteredWorksheets.filter(w => !w.isFree).length;

  // Generate contextual heading
  const getContextualHeading = () => {
    if (selectedCategory !== 'All' && selectedTopic !== 'All Topics') {
      return `Worksheets for ${selectedCategory} — ${selectedTopic}`;
    } else if (selectedCategory !== 'All') {
      return `Worksheets for ${selectedCategory}`;
    } else if (selectedTopic !== 'All Topics') {
      return `${selectedTopic} Worksheets`;
    }
    return 'Free Math Worksheets';
  };

  const isFiltered = selectedCategory !== 'All' || selectedSubject !== 'All Subjects' || selectedTopic !== 'All Topics' || searchQuery;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <SEOHead
        title="Free Math Worksheets K-12 | Printable PDF Download | Math Mastery"
        description="Download 150+ free printable math worksheets for all grade levels — Kindergarten through Pre-Calculus, plus ACT, SAT, and state test prep. No signup required. Instant PDF download."
        keywords="free math worksheets, printable math worksheets, K-12 worksheets, ACT math worksheets, algebra worksheets, geometry worksheets, calculus worksheets"
        canonicalPath="/worksheets"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Free Math Worksheets K-12',
          description: 'Free printable math worksheets for all grade levels from Kindergarten through Pre-Calculus, plus test prep materials.',
          provider: { '@type': 'EducationalOrganization', name: 'Math Mastery' },
        }}
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-orange-500 to-orange-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">{getContextualHeading()}</h1>
            <p className="text-xl mb-8 text-orange-50 max-w-3xl mx-auto">
              {isFiltered 
                ? `Browse ${filteredWorksheets.length} worksheets matching your selection. Download and print instantly.`
                : 'Download high-quality, printable math worksheets for all grade levels. From Kindergarten to Pre-Calculus, plus SAT, ACT, and State Test prep materials.'
              }
            </p>
            <div className="flex items-center justify-center gap-8 text-lg">
              <div className="flex items-center gap-2">
                <i className="ri-file-text-line text-2xl"></i>
                <span className="font-semibold">{allWorksheets.length}+ Worksheets</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-download-line text-2xl"></i>
                <span className="font-semibold">Instant Download</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-printer-line text-2xl"></i>
                <span className="font-semibold">Print Ready</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links for ACT Math */}
      {selectedCategory === 'ACT Math' && (
        <div className="bg-gradient-to-r from-violet-50 to-purple-50 border-b border-violet-100">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-center gap-4">
              <i className="ri-information-line text-violet-600"></i>
              <span className="text-sm text-gray-700">Looking for ACT Math worksheets organized by domain?</span>
              <a
                href="/act-math-worksheets"
                className="px-4 py-2 bg-violet-600 text-white rounded-lg font-semibold text-sm hover:bg-violet-700 transition-colors whitespace-nowrap"
              >
                <i className="ri-file-list-3-line mr-2"></i>
                Browse ACT Math Worksheets
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Quick Links for Grade 9 */}
      {selectedCategory === 'Grade 9' && (
        <div className="bg-gradient-to-r from-orange-50 to-red-50 border-b border-orange-100">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-center gap-4">
              <i className="ri-information-line text-orange-600"></i>
              <span className="text-sm text-gray-700">Looking for Grade 9 worksheets organized by topic?</span>
              <a
                href="/grade-9-worksheets"
                className="px-4 py-2 bg-orange-600 text-white rounded-lg font-semibold text-sm hover:bg-orange-700 transition-colors whitespace-nowrap"
              >
                <i className="ri-file-list-3-line mr-2"></i>
                Browse Grade 9 Worksheets
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Quick Links for Grade 11 */}
      {selectedCategory === 'Grade 11' && (
        <div className="bg-gradient-to-r from-rose-50 to-pink-50 border-b border-rose-100">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-center gap-4">
              <i className="ri-information-line text-rose-600"></i>
              <span className="text-sm text-gray-700">Looking for Grade 11 worksheets organized by topic?</span>
              <a
                href="/grade-11-worksheets"
                className="px-4 py-2 bg-rose-600 text-white rounded-lg font-semibold text-sm hover:bg-rose-700 transition-colors whitespace-nowrap"
              >
                <i className="ri-file-list-3-line mr-2"></i>
                Browse Grade 11 Worksheets
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Filter Section */}
      <section className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-6">
          {/* Search Bar */}
          <div className="mb-6">
            <div className="relative max-w-2xl mx-auto">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl"></i>
              <input
                type="text"
                placeholder="Search worksheets by title, topic, or keyword..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Grade Level Filter */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Grade Level</label>
              <select
                value={selectedCategory}
                onChange={(e) => handleCategoryChange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent cursor-pointer"
              >
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>

            {/* Subject Filter */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Subject</label>
              <select
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent cursor-pointer"
              >
                {subjects.map((subject) => (
                  <option key={subject} value={subject}>
                    {subject}
                  </option>
                ))}
              </select>
            </div>

            {/* Topic Filter */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Topic</label>
              <select
                value={selectedTopic}
                onChange={(e) => handleTopicChange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent cursor-pointer"
              >
                {topics.map((topic) => (
                  <option key={topic} value={topic}>
                    {topic}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Results Count */}
          <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
            <div>
              Showing <span className="font-semibold text-gray-900">{filteredWorksheets.length}</span> worksheets
              {isFiltered && (
                <button
                  onClick={clearFilters}
                  className="ml-4 text-orange-600 hover:text-orange-700 font-medium whitespace-nowrap"
                >
                  Clear Filters
                </button>
              )}
            </div>
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <i className="ri-unlock-line text-green-600"></i>
                <span className="font-semibold text-green-600">{freeCount} Free</span>
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-vip-crown-line text-orange-600"></i>
                <span className="font-semibold text-orange-600">{premiumCount} Members Only</span>
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Worksheets Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6">
          {filteredWorksheets.length === 0 ? (
            <div className="text-center py-20">
              <i className="ri-file-search-line text-6xl text-gray-300 mb-4"></i>
              <h3 className="text-2xl font-bold text-gray-700 mb-2">No worksheets found</h3>
              <p className="text-gray-500 mb-6">Try adjusting your filters or search query</p>
              <button
                onClick={clearFilters}
                className="px-6 py-3 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition-colors whitespace-nowrap"
              >
                Clear All Filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredWorksheets.map((worksheet) => (
                <div
                  key={worksheet.id}
                  className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow group"
                >
                  {/* Thumbnail */}
                  <div className="relative w-full h-64 bg-gray-100 overflow-hidden">
                    <img
                      src={worksheet.thumbnailUrl}
                      alt={worksheet.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                    {/* Free/Premium Badge */}
                    <div className="absolute top-3 right-3">
                      {worksheet.isFree ? (
                        <span className="px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full flex items-center gap-1 whitespace-nowrap">
                          <i className="ri-unlock-line"></i>
                          FREE
                        </span>
                      ) : (
                        <span className="px-3 py-1 bg-orange-500 text-white text-xs font-bold rounded-full flex items-center gap-1 whitespace-nowrap">
                          <i className="ri-vip-crown-line"></i>
                          MEMBERS
                        </span>
                      )}
                    </div>
                    {/* Difficulty Badge */}
                    <div className="absolute top-3 left-3">
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${
                        worksheet.difficulty === 'Beginner' ? 'bg-blue-100 text-blue-700' :
                        worksheet.difficulty === 'Intermediate' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {worksheet.difficulty}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    {/* Grade & Subject */}
                    <div className="flex items-center gap-2 mb-2 text-xs text-gray-500">
                      <span className="font-semibold">{worksheet.grade}</span>
                      <span>•</span>
                      <span>{worksheet.subject}</span>
                    </div>

                    {/* Title */}
                    <h3 className="text-base font-bold text-gray-900 mb-2 line-clamp-2">
                      {worksheet.title}
                    </h3>

                    {/* Description */}
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {worksheet.description}
                    </p>

                    {/* Topic Tag */}
                    <div className="mb-3">
                      <span className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded whitespace-nowrap">
                        {worksheet.topic}
                      </span>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <i className="ri-file-text-line"></i>
                        {worksheet.pages} pages
                      </span>
                      <button className="px-4 py-2 bg-orange-500 text-white text-sm font-semibold rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2 whitespace-nowrap">
                        <i className="ri-download-line"></i>
                        Download
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-orange-500 to-orange-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <i className="ri-vip-crown-line text-5xl mb-4"></i>
          <h2 className="text-3xl font-bold mb-4">Want Access to All Premium Worksheets?</h2>
          <p className="text-lg text-orange-50 mb-8">
            Become a member and unlock {premiumCount}+ premium worksheets, plus unlimited access to all our learning materials and personalized tutoring.
          </p>
          <a
            href="#pricing"
            className="inline-block px-8 py-4 bg-white text-orange-600 font-bold text-lg rounded-lg hover:bg-orange-50 transition-colors whitespace-nowrap"
          >
            View Membership Plans
          </a>
        </div>
      </section>
    </div>
  );
}